// Iter 83.3 / Phase E4 Sprint 3 — Exit + FNF Console.
// Operator surface for the full Exit lifecycle + FNF settlement +
// Freeze Certificate. Heavy page — kept as one file for cohesion.
import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Plus, CheckCircle, XCircle, MagnifyingGlass, Briefcase,
    UserMinus, Calculator, FileText, ListChecks, Warning,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";

const EXIT_STATUS_TONE = {
    Initiated: "bg-amber-100 text-amber-800",
    NoticePeriodActive: "bg-sky-100 text-sky-800",
    ClearanceInProgress: "bg-violet-100 text-violet-800",
    ClearanceComplete: "bg-emerald-100 text-emerald-800",
    FNFCalculated: "bg-blue-100 text-blue-800",
    FNFApproved: "bg-emerald-100 text-emerald-800",
    FNFPaid: "bg-emerald-200 text-emerald-900",
    ExitClosed: "bg-slate-200 text-slate-700",
};

const FNF_STATUS_TONE = {
    Draft: "bg-zinc-100 text-zinc-700",
    Calculated: "bg-blue-100 text-blue-800",
    PendingApproval: "bg-amber-100 text-amber-800",
    Approved: "bg-emerald-100 text-emerald-800",
    Paid: "bg-emerald-200 text-emerald-900",
    Closed: "bg-slate-200 text-slate-700",
    Voided: "bg-rose-100 text-rose-700",
};

const fmtMoney = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ExitFnfConsole() {
    const { can, user } = useAuth();
    const canInitiate = can("exit:admin");
    const canCalc = can("fnf:admin");
    const canApprove = can("fnf:approve") || can("finance:approve");
    const [items, setItems] = useState([]);
    const [meta, setMeta] = useState(null);
    const [fnfMeta, setFnfMeta] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    // Initiate dialog
    const [initOpen, setInitOpen] = useState(false);
    const [initForm, setInitForm] = useState({
        employee_id: "", exit_type: "Resignation", exit_reason: "",
        date_of_notice: "", last_working_day: "",
        notice_period_days_required: 30,
        nominee: { name: "", relationship: "", contact_phone: "", contact_email: "", address: "" },
    });
    // Detail dialog
    const [detail, setDetail] = useState(null);
    const [checklist, setChecklist] = useState([]);
    const [gate, setGate] = useState(null);
    const [fnf, setFnf] = useState(null);
    const [fnfAudit, setFnfAudit] = useState([]);
    // Calculate dialog
    const [calcOpen, setCalcOpen] = useState(false);
    const [calcForm, setCalcForm] = useState({
        period_start_iso: "", days_worked: 22,
        last_drawn_basic: 40000, last_drawn_da: 0, last_drawn_gross: 60000,
        gratuity_mode: "statutory", gratuity_dsl_amount: "",
        encashable_leave_days: 0,
        notice_recovery_mode: "full", partial_waiver_days: 0,
        fytd_tds_refund: 0, fytd_tds_extra_deduction: 0,
    });

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (statusFilter) params.status = statusFilter;
            const [list, m, fm, emp] = await Promise.all([
                api.get("/exit/list", { params }),
                api.get("/exit/meta"),
                api.get("/fnf/meta"),
                api.get("/hr/employee/list", { params: { limit: 500 } }).catch(() => ({ data: { items: [] } })),
            ]);
            setItems(list.data.items);
            setMeta(m.data);
            setFnfMeta(fm.data);
            setEmployees(emp.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load exits"));
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);
    useEffect(() => { reload(); }, [reload]);

    const filtered = useMemo(() => {
        if (!search) return items;
        const q = search.toLowerCase();
        return items.filter((it) =>
            (it.id || "").toLowerCase().includes(q)
            || ((it.employee_snapshot || {}).full_name || "").toLowerCase().includes(q)
            || (it.exit_type || "").toLowerCase().includes(q)
        );
    }, [items, search]);

    const submitInitiate = async () => {
        if (!initForm.employee_id || !initForm.date_of_notice || !initForm.last_working_day) {
            toast.error("Employee, notice date, and LWD are required");
            return;
        }
        const payload = {
            employee_id: initForm.employee_id,
            exit_type: initForm.exit_type,
            exit_reason: initForm.exit_reason,
            date_of_notice: initForm.date_of_notice,
            last_working_day: initForm.last_working_day,
            notice_period_days_required: Number(initForm.notice_period_days_required),
        };
        if (initForm.exit_type === "Demise") {
            if (!initForm.nominee.name?.trim() || !initForm.nominee.relationship?.trim()) {
                toast.error("Nominee details required for Demise");
                return;
            }
            payload.nominee = initForm.nominee;
        }
        try {
            const { data } = await api.post("/exit/initiate", payload);
            toast.success(`Exit ${data.id} initiated`);
            setInitOpen(false);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Initiate failed"));
        }
    };

    const openDetail = async (it) => {
        setDetail(it);
        setChecklist([]);
        setGate(null);
        setFnf(null);
        setFnfAudit([]);
        try {
            const [cl, g] = await Promise.all([
                api.get(`/exit/${it.id}/checklist`),
                api.get(`/exit/${it.id}/clearance-gate`),
            ]);
            setChecklist(cl.data.items);
            setGate(g.data);
            if (it.fnf_id) {
                const [f, a] = await Promise.all([
                    api.get(`/fnf/${it.fnf_id}`),
                    api.get(`/fnf/${it.fnf_id}/audit`),
                ]);
                setFnf(f.data);
                setFnfAudit(a.data.items);
            }
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load detail"));
        }
    };

    const toggleChecklistItem = async (item, newStatus) => {
        if (!detail) return;
        try {
            await api.patch(`/exit/${detail.id}/checklist/${item.id}`, {
                status: newStatus, notes: item.notes || "",
            });
            const cl = await api.get(`/exit/${detail.id}/checklist`);
            setChecklist(cl.data.items);
            const g = await api.get(`/exit/${detail.id}/clearance-gate`);
            setGate(g.data);
        } catch (err) {
            toast.error(formatApiError(err, "Update failed"));
        }
    };

    const openCalc = () => {
        if (!detail) return;
        // Pre-fill period_start to month-start of LWD
        const lwd = detail.last_working_day;
        const periodStart = lwd ? `${lwd.substring(0, 7)}-01` : "";
        setCalcForm({
            ...calcForm,
            period_start_iso: periodStart,
        });
        setCalcOpen(true);
    };

    const submitCalc = async () => {
        if (!detail) return;
        const payload = {
            exit_id: detail.id,
            period_start_iso: calcForm.period_start_iso,
            days_worked: Number(calcForm.days_worked),
            last_drawn_basic: Number(calcForm.last_drawn_basic),
            last_drawn_da: Number(calcForm.last_drawn_da),
            last_drawn_gross: Number(calcForm.last_drawn_gross),
            gratuity_mode: calcForm.gratuity_mode,
            encashable_leave_days: Number(calcForm.encashable_leave_days),
            notice_recovery_mode: calcForm.notice_recovery_mode,
            partial_waiver_days: Number(calcForm.partial_waiver_days),
            fytd_tds_refund: Number(calcForm.fytd_tds_refund),
            fytd_tds_extra_deduction: Number(calcForm.fytd_tds_extra_deduction),
        };
        if (calcForm.gratuity_mode === "dsl" && calcForm.gratuity_dsl_amount !== "") {
            payload.gratuity_dsl_amount = Number(calcForm.gratuity_dsl_amount);
        }
        try {
            const { data } = await api.post("/fnf/calculate", payload);
            toast.success(`FNF ${data.id} calculated (net ${fmtMoney(data.net_settlement_amount)})`);
            setCalcOpen(false);
            await openDetail({ ...detail, fnf_id: data.id });
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Calculate failed"));
        }
    };

    const submitFnf = async () => {
        if (!fnf) return;
        try {
            await api.post(`/fnf/${fnf.id}/submit`, { note: "Submitted via console" });
            toast.success("Submitted for approval");
            await openDetail(detail);
        } catch (err) {
            toast.error(formatApiError(err, "Submit failed"));
        }
    };

    const approveFnf = async () => {
        if (!fnf) return;
        let negNote = "";
        if (fnf.is_negative) {
            negNote = window.prompt(
                `Net FNF is NEGATIVE (${fmtMoney(fnf.shortfall_amount)} shortfall). Enter approval note (D23):`
            );
            if (!negNote?.trim()) {
                toast.error("D23 — approval note required for negative FNF");
                return;
            }
        }
        if (!window.confirm(`Approve FNF ${fnf.id}? Net: ${fmtMoney(fnf.net_settlement_amount)}.`)) return;
        try {
            await api.post(`/fnf/${fnf.id}/approve`, {
                approver_note: "Approved via console",
                negative_fnf_outstanding_note: negNote,
            });
            toast.success("Approved (Freeze Certificate sealed)");
            await openDetail(detail);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Approve failed"));
        }
    };

    const markPaid = async () => {
        if (!fnf) return;
        const ref = window.prompt("Bank reference number:");
        if (ref === null) return;
        try {
            await api.post(`/fnf/${fnf.id}/mark-paid`, { reference_number: ref });
            toast.success("Marked paid");
            await openDetail(detail);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Mark paid failed"));
        }
    };

    const voidFnf = async () => {
        if (!fnf) return;
        const reason = window.prompt("Void reason (D20 — will require reissue):");
        if (!reason?.trim()) return;
        try {
            await api.post(`/fnf/${fnf.id}/void`, { reason });
            toast.success("Voided. Issue a new FNF via Calculate.");
            await openDetail(detail);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Void failed"));
        }
    };

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1700px]" data-testid="exit-fnf-console-page">
            <PageHeader
                eyebrow="Exit & FNF"
                title="Exit Management"
                description={`${items.length} exit event${items.length === 1 ? "" : "s"} on file · D24 4-eye · D26/D27 gates · D28 Freeze Certificate`}
                actions={canInitiate ? (
                    <Button onClick={() => setInitOpen(true)} size="sm" data-testid="exit-new-btn">
                        <Plus size={14} className="mr-1" /> Initiate Exit
                    </Button>
                ) : null}
            />
            <section className="mt-5 bg-white rounded-xl border border-slate-200 p-4">
                <div className="flex gap-3 flex-wrap items-end">
                    <div className="flex-1 min-w-[240px]">
                        <Label className="text-[10px] uppercase text-slate-500 font-semibold">Search</Label>
                        <div className="relative">
                            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input className="pl-9" placeholder="Exit ID, employee, type…"
                                value={search} onChange={(e) => setSearch(e.target.value)}
                                data-testid="exit-search" />
                        </div>
                    </div>
                    <div className="w-48">
                        <Label className="text-[10px] uppercase text-slate-500 font-semibold">Status</Label>
                        <select className="w-full border rounded-md text-sm h-9 px-2"
                            value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                            data-testid="exit-status-filter">
                            <option value="">All</option>
                            {(meta?.exit_states || []).map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                </div>
            </section>
            <section className="mt-4 bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="exit-table">
                {loading ? <div className="text-center py-12 text-sm text-slate-400">Loading…</div> : (
                    <table className="w-full text-sm min-w-[1200px]">
                        <thead className="bg-slate-50 text-slate-600 text-xs">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Exit ID</th>
                                <th className="text-left px-3 py-2 font-medium">Employee</th>
                                <th className="text-left px-3 py-2 font-medium">Type</th>
                                <th className="text-left px-3 py-2 font-medium">Notice → LWD</th>
                                <th className="text-left px-3 py-2 font-medium">Status</th>
                                <th className="text-left px-3 py-2 font-medium">FNF</th>
                                <th className="text-left px-3 py-2 font-medium"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((it) => (
                                <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`exit-row-${it.id}`}>
                                    <td className="px-3 py-2 font-mono text-xs">{it.id}</td>
                                    <td className="px-3 py-2">{(it.employee_snapshot || {}).full_name}</td>
                                    <td className="px-3 py-2 text-xs">
                                        {it.exit_type}
                                        {it.is_absconding && <Badge className="ml-1 bg-rose-100 text-rose-700 text-[9px]">ABSCOND</Badge>}
                                    </td>
                                    <td className="px-3 py-2 text-xs font-mono">{it.date_of_notice?.slice(0, 10)} → {it.last_working_day?.slice(0, 10)}</td>
                                    <td className="px-3 py-2"><Badge className={`text-[10px] ${EXIT_STATUS_TONE[it.status] || ""}`}>{it.status}</Badge></td>
                                    <td className="px-3 py-2 text-xs font-mono">{it.fnf_id || <span className="text-slate-400">—</span>}</td>
                                    <td className="px-3 py-2">
                                        <Button variant="ghost" size="sm" onClick={() => openDetail(it)} data-testid={`exit-view-${it.id}`}>
                                            <ListChecks size={14} />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                            {filtered.length === 0 && (
                                <tr><td colSpan={7} className="text-center py-10 text-slate-400 italic text-sm">No exit events.</td></tr>
                            )}
                        </tbody>
                    </table>
                )}
            </section>

            {/* Initiate dialog */}
            <Dialog open={initOpen} onOpenChange={setInitOpen}>
                <DialogContent className="max-w-xl" data-testid="exit-initiate-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><UserMinus size={18} /> Initiate Exit</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="col-span-2">
                            <Label>Employee *</Label>
                            <select className="w-full border rounded-md text-sm h-9 px-2 mt-1"
                                value={initForm.employee_id}
                                onChange={(e) => setInitForm({ ...initForm, employee_id: e.target.value })}
                                data-testid="exit-init-emp">
                                <option value="">— Select —</option>
                                {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name} ({e.id})</option>)}
                            </select>
                        </div>
                        <div>
                            <Label>Type *</Label>
                            <select className="w-full border rounded-md text-sm h-9 px-2 mt-1"
                                value={initForm.exit_type}
                                onChange={(e) => setInitForm({ ...initForm, exit_type: e.target.value })}
                                data-testid="exit-init-type">
                                {(meta?.exit_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>
                        <div>
                            <Label>Notice Period (days)</Label>
                            <Input type="number" min="0" value={initForm.notice_period_days_required}
                                onChange={(e) => setInitForm({ ...initForm, notice_period_days_required: e.target.value })} />
                        </div>
                        <div>
                            <Label>Date of Notice *</Label>
                            <Input type="date" value={initForm.date_of_notice}
                                onChange={(e) => setInitForm({ ...initForm, date_of_notice: e.target.value })}
                                data-testid="exit-init-notice" />
                        </div>
                        <div>
                            <Label>Last Working Day *</Label>
                            <Input type="date" value={initForm.last_working_day}
                                onChange={(e) => setInitForm({ ...initForm, last_working_day: e.target.value })}
                                data-testid="exit-init-lwd" />
                        </div>
                        <div className="col-span-2">
                            <Label>Reason / Notes</Label>
                            <Input value={initForm.exit_reason}
                                onChange={(e) => setInitForm({ ...initForm, exit_reason: e.target.value })} />
                        </div>
                        {initForm.exit_type === "Demise" && (
                            <div className="col-span-2 p-3 bg-rose-50 border border-rose-200 rounded space-y-2">
                                <div className="text-xs font-semibold text-rose-800">Nominee details (required for Demise)</div>
                                <div className="grid grid-cols-2 gap-2">
                                    <Input placeholder="Name *" value={initForm.nominee.name}
                                        onChange={(e) => setInitForm({ ...initForm, nominee: { ...initForm.nominee, name: e.target.value } })} />
                                    <Input placeholder="Relationship *" value={initForm.nominee.relationship}
                                        onChange={(e) => setInitForm({ ...initForm, nominee: { ...initForm.nominee, relationship: e.target.value } })} />
                                    <Input placeholder="Phone" value={initForm.nominee.contact_phone}
                                        onChange={(e) => setInitForm({ ...initForm, nominee: { ...initForm.nominee, contact_phone: e.target.value } })} />
                                    <Input placeholder="Email" value={initForm.nominee.contact_email}
                                        onChange={(e) => setInitForm({ ...initForm, nominee: { ...initForm.nominee, contact_email: e.target.value } })} />
                                </div>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setInitOpen(false)}>Cancel</Button>
                        <Button onClick={submitInitiate} data-testid="exit-init-submit">Initiate</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Detail dialog — includes checklist + gate + FNF panel */}
            <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
                <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="exit-detail-dialog">
                    {detail && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                    <UserMinus size={18} /> {detail.id}
                                    <Badge className={`text-[10px] ${EXIT_STATUS_TONE[detail.status] || ""} ml-2`}>{detail.status}</Badge>
                                    <Badge className="ml-1 bg-slate-100 text-slate-700 text-[10px]">{detail.exit_type}</Badge>
                                </DialogTitle>
                            </DialogHeader>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                    <div className="text-[10px] uppercase text-slate-500 font-semibold">Employee</div>
                                    <div>{(detail.employee_snapshot || {}).full_name}</div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase text-slate-500 font-semibold">Notice → LWD</div>
                                    <div className="font-mono text-xs">{detail.date_of_notice} → {detail.last_working_day} ({detail.notice_period_days_served}/{detail.notice_period_days_required} days served)</div>
                                </div>
                                {detail.nominee && (
                                    <div className="col-span-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs">
                                        <div className="font-semibold">Nominee</div>
                                        {detail.nominee.name} · {detail.nominee.relationship} · {detail.nominee.contact_phone || "—"}
                                    </div>
                                )}
                            </div>

                            {/* Gate report */}
                            {gate && (
                                <div className="mt-3 p-3 border border-slate-200 rounded space-y-2" data-testid="clearance-gate">
                                    <div className="text-xs font-semibold uppercase text-slate-600 flex items-center gap-2">
                                        <Warning size={14} /> Clearance Gates
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 text-xs">
                                        <div className={`p-2 rounded ${gate.d27_checklist_gate.passes_gate ? "bg-emerald-50 border border-emerald-200" : "bg-rose-50 border border-rose-200"}`}>
                                            <div className="font-semibold">D27 Checklist Gate</div>
                                            <div>{gate.d27_checklist_gate.mandatory_complete}/{gate.d27_checklist_gate.mandatory_total} mandatory complete</div>
                                            <div className="font-mono text-[10px] mt-1">{gate.d27_checklist_gate.passes_gate ? "✓ PASSES" : "✗ BLOCKS"}</div>
                                        </div>
                                        <div className={`p-2 rounded ${gate.d26_asset_gate.passes_gate ? "bg-emerald-50 border border-emerald-200" : "bg-rose-50 border border-rose-200"}`}>
                                            <div className="font-semibold">D26 Asset Clearance Gate</div>
                                            <div>{gate.d26_asset_gate.open_assignment_count} open · ₹{Number(gate.d26_asset_gate.total_depreciated_value_at_risk).toLocaleString("en-IN")} at risk</div>
                                            <div className="font-mono text-[10px] mt-1">{gate.d26_asset_gate.passes_gate ? "✓ PASSES" : "✗ BLOCKS"}</div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Checklist */}
                            <div className="mt-4">
                                <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1">Exit Clearance Checklist (D27)</div>
                                <div className="overflow-x-auto border border-slate-200 rounded">
                                    <table className="w-full text-xs" data-testid="checklist-table">
                                        <thead className="bg-slate-50 text-slate-600">
                                            <tr>
                                                <th className="text-left px-2 py-1">Item</th>
                                                <th className="text-left px-2 py-1">Dept</th>
                                                <th className="text-left px-2 py-1">Mandatory</th>
                                                <th className="text-left px-2 py-1">Status</th>
                                                <th className="text-left px-2 py-1">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {checklist.map((it) => (
                                                <tr key={it.id} className="border-t border-slate-100" data-testid={`checklist-row-${it.item_code}`}>
                                                    <td className="px-2 py-1">{it.item_label}</td>
                                                    <td className="px-2 py-1 text-slate-500">{it.department}</td>
                                                    <td className="px-2 py-1">{it.is_mandatory ? "Yes" : "No"}</td>
                                                    <td className="px-2 py-1">
                                                        <Badge className={`text-[10px] ${it.status === "Complete" ? "bg-emerald-100 text-emerald-800" : it.status === "Waived" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"}`}>
                                                            {it.status}
                                                        </Badge>
                                                    </td>
                                                    <td className="px-2 py-1">
                                                        {canInitiate && it.status !== "Complete" && (
                                                            <Button size="sm" variant="ghost" onClick={() => toggleChecklistItem(it, "Complete")} data-testid={`checklist-complete-${it.item_code}`}>
                                                                <CheckCircle size={12} className="text-emerald-600" />
                                                            </Button>
                                                        )}
                                                        {canInitiate && it.status === "Complete" && (
                                                            <Button size="sm" variant="ghost" onClick={() => toggleChecklistItem(it, "Pending")}>
                                                                <XCircle size={12} className="text-slate-500" />
                                                            </Button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* FNF section */}
                            <div className="mt-4 border-t border-slate-200 pt-4">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="text-sm font-semibold flex items-center gap-2"><Calculator size={16} /> FNF Settlement</div>
                                    <div className="flex gap-2">
                                        {!fnf && canCalc && (
                                            <Button size="sm" onClick={openCalc} data-testid="fnf-calc-btn">
                                                <Calculator size={14} className="mr-1" /> Calculate
                                            </Button>
                                        )}
                                        {fnf && (fnf.status === "Calculated") && canCalc && (
                                            <>
                                                <Button size="sm" onClick={openCalc} variant="outline">Recalculate</Button>
                                                <Button size="sm" onClick={submitFnf} data-testid="fnf-submit-btn">Submit for approval</Button>
                                            </>
                                        )}
                                        {fnf && fnf.status === "PendingApproval" && canApprove && (
                                            <Button size="sm" onClick={approveFnf} data-testid="fnf-approve-btn">
                                                <CheckCircle size={14} className="mr-1" /> Approve
                                            </Button>
                                        )}
                                        {fnf && fnf.status === "Approved" && canCalc && (
                                            <Button size="sm" onClick={markPaid} data-testid="fnf-pay-btn">Mark Paid</Button>
                                        )}
                                        {fnf && (fnf.status === "Approved" || fnf.status === "Paid") && canApprove && (
                                            <Button size="sm" variant="outline" onClick={voidFnf}>Void</Button>
                                        )}
                                        {fnf && fnf.status === "Approved" && fnf.certificate_id && (
                                            <a href={`/fnf/${fnf.id}/certificate/print`} target="_blank" rel="noreferrer">
                                                <Button size="sm" variant="outline" data-testid="fnf-cert-btn">
                                                    <FileText size={14} className="mr-1" /> Freeze Certificate
                                                </Button>
                                            </a>
                                        )}
                                        {fnf && (fnf.status === "Approved" || fnf.status === "Paid" || fnf.status === "Closed") && (
                                            <a href={`/fnf/${fnf.id}/statement/print`} target="_blank" rel="noreferrer">
                                                <Button size="sm" variant="outline" data-testid="fnf-statement-btn">
                                                    <FileText size={14} className="mr-1" /> Statement (Sign-off)
                                                </Button>
                                            </a>
                                        )}
                                        {fnf && (fnf.status === "Approved" || fnf.status === "Paid" || fnf.status === "Closed") && (
                                            <a href={`/fnf/${fnf.id}/relieving-letter/print`} target="_blank" rel="noreferrer">
                                                <Button size="sm" variant="outline" data-testid="fnf-relieving-btn">
                                                    <FileText size={14} className="mr-1" /> Relieving Letter
                                                </Button>
                                            </a>
                                        )}
                                        {fnf && (fnf.status === "Approved" || fnf.status === "Paid" || fnf.status === "Closed") && !["Termination", "Absconding"].includes(fnf.exit_type) && (
                                            <a href={`/fnf/${fnf.id}/experience-certificate/print`} target="_blank" rel="noreferrer">
                                                <Button size="sm" variant="outline" data-testid="fnf-experience-btn">
                                                    <FileText size={14} className="mr-1" /> Experience Certificate
                                                </Button>
                                            </a>
                                        )}
                                    </div>
                                </div>
                                {fnf ? (
                                    <>
                                        <div className="grid grid-cols-4 gap-2 text-xs">
                                            <div className="p-2 bg-slate-50 border rounded">
                                                <div className="text-[10px] uppercase font-semibold text-slate-500">FNF ID</div>
                                                <div className="font-mono">{fnf.id}</div>
                                                <Badge className={`text-[10px] mt-1 ${FNF_STATUS_TONE[fnf.status] || ""}`}>{fnf.status}</Badge>
                                            </div>
                                            <div className="p-2 bg-emerald-50 border border-emerald-200 rounded">
                                                <div className="text-[10px] uppercase font-semibold text-emerald-700">Earnings subtotal</div>
                                                <div className="font-mono">{fmtMoney(fnf.earnings_subtotal)}</div>
                                            </div>
                                            <div className="p-2 bg-rose-50 border border-rose-200 rounded">
                                                <div className="text-[10px] uppercase font-semibold text-rose-700">Deductions subtotal</div>
                                                <div className="font-mono">{fmtMoney(fnf.deductions_subtotal)}</div>
                                            </div>
                                            <div className={`p-2 border rounded ${fnf.is_negative ? "bg-rose-100 border-rose-300" : "bg-emerald-100 border-emerald-300"}`}>
                                                <div className="text-[10px] uppercase font-semibold">Net</div>
                                                <div className="font-mono font-bold">{fmtMoney(fnf.net_settlement_amount)}</div>
                                                {fnf.is_negative && <Badge className="bg-rose-200 text-rose-900 text-[10px] mt-1">D23 NEGATIVE</Badge>}
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3 text-xs mt-3">
                                            <div className="border rounded p-2">
                                                <div className="font-semibold mb-1">Earnings</div>
                                                <div>Pro-rata: {fmtMoney(fnf.pro_rata_total)}</div>
                                                <div>Gratuity (D21 {fnf.gratuity?.formula_used}): {fmtMoney(fnf.gratuity?.amount)}</div>
                                                <div>Leave Encashment (D22 basic+da, {fnf.leave_encashment?.encashable_days} days): {fmtMoney(fnf.leave_encashment?.amount)}</div>
                                                <div>FYTD TDS Refund: {fmtMoney(fnf.fytd_tds_reconciliation?.refund_added_to_earnings)}</div>
                                            </div>
                                            <div className="border rounded p-2">
                                                <div className="font-semibold mb-1">Deductions</div>
                                                <div>Notice Recovery ({fnf.notice_recovery?.mode}, {fnf.notice_recovery?.effective_short_days} days): {fmtMoney(fnf.notice_recovery?.amount)}</div>
                                                <div>Loan Recovery (D16): {fmtMoney(fnf.loan_recovery?.amount)}</div>
                                                <div>Asset Recovery (D18): {fmtMoney(fnf.asset_recovery?.amount)}</div>
                                                <div>FYTD TDS Extra: {fmtMoney(fnf.fytd_tds_reconciliation?.extra_deduction)}</div>
                                            </div>
                                        </div>
                                        {fnf.certificate_hash && (
                                            <div className="mt-3 p-2 bg-emerald-50 border border-emerald-300 rounded text-xs">
                                                <div className="font-semibold text-emerald-900 flex items-center gap-1">
                                                    <FileText size={12} /> D28 Freeze Certificate
                                                </div>
                                                <div className="font-mono text-[10px] mt-1 break-all">SHA-256: {fnf.certificate_hash}</div>
                                                <div className="text-emerald-700">Sealed at {fnf.approved_at}</div>
                                            </div>
                                        )}
                                        {fnfAudit.length > 0 && (
                                            <div className="mt-3">
                                                <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1">FNF Audit Trail</div>
                                                <ul className="text-xs space-y-1">
                                                    {fnfAudit.map((a) => (
                                                        <li key={a.id} className="font-mono">
                                                            <span className="text-slate-500">{(a.created_at || "").slice(0, 19).replace("T", " ")}</span>
                                                            {" · "}
                                                            <span className="font-semibold">{a.action}</span>
                                                            {a.from_state && <span> ({a.from_state} → {a.to_state})</span>}
                                                            <span className="text-slate-500"> · {a.actor_email}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="text-xs text-slate-500 italic">No FNF yet. Complete the clearance checklist + click Calculate.</div>
                                )}
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Calculate dialog */}
            <Dialog open={calcOpen} onOpenChange={setCalcOpen}>
                <DialogContent className="max-w-2xl" data-testid="fnf-calc-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><Calculator size={18} /> Calculate FNF</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                            <Label>Period Start ISO *</Label>
                            <Input type="date" value={calcForm.period_start_iso}
                                onChange={(e) => setCalcForm({ ...calcForm, period_start_iso: e.target.value })}
                                data-testid="calc-period-start" />
                        </div>
                        <div>
                            <Label>Days Worked *</Label>
                            <Input type="number" min="0" value={calcForm.days_worked}
                                onChange={(e) => setCalcForm({ ...calcForm, days_worked: e.target.value })} />
                        </div>
                        <div>
                            <Label>Last Drawn Basic (₹)</Label>
                            <Input type="number" min="0" value={calcForm.last_drawn_basic}
                                onChange={(e) => setCalcForm({ ...calcForm, last_drawn_basic: e.target.value })} />
                        </div>
                        <div>
                            <Label>Last Drawn DA (₹)</Label>
                            <Input type="number" min="0" value={calcForm.last_drawn_da}
                                onChange={(e) => setCalcForm({ ...calcForm, last_drawn_da: e.target.value })} />
                        </div>
                        <div>
                            <Label>Last Drawn Gross (₹)</Label>
                            <Input type="number" min="0" value={calcForm.last_drawn_gross}
                                onChange={(e) => setCalcForm({ ...calcForm, last_drawn_gross: e.target.value })} />
                        </div>
                        <div>
                            <Label>Encashable Leave Days</Label>
                            <Input type="number" min="0" step="0.5" value={calcForm.encashable_leave_days}
                                onChange={(e) => setCalcForm({ ...calcForm, encashable_leave_days: e.target.value })} />
                        </div>
                        <div>
                            <Label>Gratuity Mode (D21)</Label>
                            <select className="w-full border rounded-md text-sm h-9 px-2 mt-1"
                                value={calcForm.gratuity_mode}
                                onChange={(e) => setCalcForm({ ...calcForm, gratuity_mode: e.target.value })}
                                data-testid="calc-grat-mode">
                                {(fnfMeta?.gratuity_modes || []).map((m) => <option key={m} value={m}>{m}</option>)}
                            </select>
                        </div>
                        {calcForm.gratuity_mode === "dsl" && (
                            <div>
                                <Label>DSL Amount (₹)</Label>
                                <Input type="number" min="0" value={calcForm.gratuity_dsl_amount}
                                    onChange={(e) => setCalcForm({ ...calcForm, gratuity_dsl_amount: e.target.value })} />
                            </div>
                        )}
                        <div>
                            <Label>Notice Recovery Mode</Label>
                            <select className="w-full border rounded-md text-sm h-9 px-2 mt-1"
                                value={calcForm.notice_recovery_mode}
                                onChange={(e) => setCalcForm({ ...calcForm, notice_recovery_mode: e.target.value })}>
                                {(fnfMeta?.notice_recovery_modes || []).map((m) => <option key={m} value={m}>{m}</option>)}
                            </select>
                        </div>
                        {calcForm.notice_recovery_mode === "partial_waiver" && (
                            <div>
                                <Label>Waiver Days</Label>
                                <Input type="number" min="0" value={calcForm.partial_waiver_days}
                                    onChange={(e) => setCalcForm({ ...calcForm, partial_waiver_days: e.target.value })} />
                            </div>
                        )}
                        <div>
                            <Label>FYTD TDS Refund (₹)</Label>
                            <Input type="number" min="0" value={calcForm.fytd_tds_refund}
                                onChange={(e) => setCalcForm({ ...calcForm, fytd_tds_refund: e.target.value })} />
                        </div>
                        <div>
                            <Label>FYTD TDS Extra Deduction (₹)</Label>
                            <Input type="number" min="0" value={calcForm.fytd_tds_extra_deduction}
                                onChange={(e) => setCalcForm({ ...calcForm, fytd_tds_extra_deduction: e.target.value })} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setCalcOpen(false)}>Cancel</Button>
                        <Button onClick={submitCalc} data-testid="calc-submit">Calculate</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
